import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { formatDate } from "../lib/utils";
import { Button } from "./ui/button";
import { ChevronLeft } from "lucide-react";
import { ImageGallery } from "./image-gallery";

interface NewsItem {
  id: number;
  title: string;
  date: string | null;
  summary: string;
  content: string;
  imageUrl: string;
  galleryImages?: string[];
}

export default function NewsItemPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [newsItem, setNewsItem] = useState<NewsItem | null>(null);

  useEffect(() => {
    if (!id) {
      navigate("/404");
      return;
    }

    fetch("https://preview.zushm.cz/api/novinky/")
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        const formattedData: NewsItem[] = data.map((item: any) => {
          const imageUrls = item.Image ? item.Image.split(",") : [];
          return {
            id: item.ID,
            title: item.Title,
            date: item.CreatedAt ? new Date(item.CreatedAt).toISOString() : null,
            summary: item.Content,
            content: item.Content,
            imageUrl: imageUrls[0] || "/placeholder.svg",
            galleryImages: [imageUrls[0], ...imageUrls.slice(1)],
          };
        });

        const selectedNewsItem = formattedData.find(
          (item) => item.id === Number.parseInt(id)
        );

        if (selectedNewsItem) {
          setNewsItem(selectedNewsItem);
        } else {
          navigate("/404");
        }
      })
      .catch((error) => console.error("Fetch error:", error));
  }, [id, navigate]);

  if (!newsItem) return null;

  return (
    <main className="flex flex-col items-center py-32 px-4 bg-white min-h-screen">
      <div className="mx-auto max-w-3xl w-full space-y-8">
        <div>
          <Button variant="ghost" className="mb-4 gap-1" onClick={() => navigate("/aktuality")}>
            <ChevronLeft className="h-4 w-4" /> Zpět
          </Button>
          <h1 className="text-4xl font-bold tracking-tight pb-2 break-words">{newsItem.title}</h1>
          <p className="text-muted-foreground mt-1">{formatDate(newsItem.date || "")}</p>
        </div>

        {newsItem.galleryImages && newsItem.galleryImages.length > 0 ? (
          <ImageGallery images={newsItem.galleryImages} alt={newsItem.title} />
        ) : (
          <div className="relative aspect-video w-full overflow-hidden rounded-lg">
            <img
              src={newsItem.imageUrl || "/placeholder.svg"}
              alt={newsItem.title}
              className="object-cover w-full h-full"
            />
          </div>
        )}

        <div
          className="text-base leading-relaxed break-words whitespace-pre-wrap space-y-4"
          style={{
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}
          dangerouslySetInnerHTML={{
            __html: `<div>${newsItem.content
              .replace(/\n{2,}/g, "</p><p>") // Two+ newlines => new paragraph
              .replace(/\n/g, "<br>") // Single newline => line break
              .replace(/^/, "<p>") // start
              .replace(/$/, "</p>") // end
            }</div>`,
          }}
        />
      </div>
    </main>
  );
}
